import React from 'react';
import { Box, Button, CircularProgress, Grow, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import {
    CheckCircleRounded as SuccessIcon,
    HistoryRounded as AlreadyIcon,
    EventBusyRounded as NoReservationIcon,
    PersonOffRounded as NotFoundIcon,
    CreditCardRounded as CardIcon,
    WifiOffRounded as ErrorIcon,
    ContactlessRounded as ReaderIcon,
    Groups2Rounded as GroupIcon,
    ConfirmationNumberRounded as TokenIcon,
    UndoRounded as UndoIcon,
} from '@mui/icons-material';
import { MealType } from '../../services/api';
import { toPersianNumber } from '../../utils/persianNumber';
import { M, MEAL_LABELS, SCAN_KIND, ScanKind, formatClock, toneColors } from './monitorTokens';
import { MealThumb, StatusPill } from './MonitorParts';

// 'available': this delivery can be undone; 'undone': it was; 'none': not undoable (not a delivery,
// or a later scan delivered the same reservation again, so only that later entry may be undone)
export type UndoState = 'none' | 'available' | 'undone';

export interface ScanEntry {
    id: string;
    at: Date;
    kind: ScanKind;
    message: string;
    identifier: string;
    mealType: MealType;
    isGroup?: boolean;
    groupTitle?: string;
    user?: { firstName: string; lastName: string; personnelCode?: string; unitTitle?: string };
    meals?: { 
        title: string; 
        imageUrl?: string | null; 
        quantity: number; 
        reservationIds?: string[];
        members?: { firstName: string; lastName: string; personnelCode?: string }[];
    }[];
    deliveredAt?: string | null;
    reservationIds?: string[]; // what this scan delivered, used to undo it
    isGuestToken?: boolean;
    description?: string | null;
}

const ICONS: Record<ScanKind, React.ReactElement> = {
    delivered: <SuccessIcon />,
    already: <AlreadyIcon />,
    noReservation: <NoReservationIcon />,
    invalidToken: <TokenIcon />,
    notFound: <NotFoundIcon />,
    unknownCard: <CardIcon />,
    error: <ErrorIcon />,
    undone: <UndoIcon />,
};

const shrink = keyframes`from { transform: scaleX(1); } to { transform: scaleX(0); }`;

const detailLine = (entry: ScanEntry) => {
    switch (entry.kind) {
        case 'already':
            return entry.deliveredAt ? `ساعت ${formatClock(entry.deliveredAt)} تحویل داده شده است` : entry.message;
        case 'noReservation':
            return `برای ${MEAL_LABELS[entry.mealType]} امروز رزروی ثبت نشده است`;
        case 'notFound':
        case 'invalidToken':
            return entry.message;
        case 'undone':
            return 'این غذا دوباره «تحویل‌نشده» ثبت شد و می‌تواند دوباره تحویل داده شود';
        case 'unknownCard':
            return 'این کارت به هیچ پرسنلی متصل نیست. صاحب کارت را در پنجره‌ی باز شده انتخاب کنید.';
        case 'error':
            return 'اتصال به سرور برقرار نشد. لطفاً دوباره اسکن کنید.';
        default:
            return entry.isGuestToken ? 'هر ژتون فقط برای یک پرس غذاست' : entry.message;
    }
};

const ScanResultPanel: React.FC<{
    entry: ScanEntry | null;
    busy: boolean;
    mealType: MealType;
    reducedMotion: boolean;
    countdownPaused: boolean;
    undoStateOf: (entry: ScanEntry) => UndoState;
    onUndo: (entry: ScanEntry) => void;
}> = ({ entry, busy, mealType, reducedMotion, countdownPaused, undoStateOf, onUndo }) => {
    if (!entry) {
        return (
            <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', textAlign: 'center', p: 3 }}>
                {busy ? (
                    <Box>
                        <CircularProgress size={64} thickness={4} sx={{ color: M.primaryText }} />
                        <Typography sx={{ fontFamily: M.font, color: M.textMuted, mt: 2, fontSize: '1.2rem' }}>در حال بررسی…</Typography>
                    </Box>
                ) : (
                    <Box>
                        <Box sx={{ width: 120, height: 120, mx: 'auto', borderRadius: '50%', bgcolor: M.primarySoft, color: M.primaryText, display: 'grid', placeItems: 'center', mb: 2.5 }}>
                            <ReaderIcon sx={{ fontSize: 64 }} />
                        </Box>
                        <Typography sx={{ fontFamily: M.font, color: M.text, fontWeight: 800, fontSize: '1.9rem' }}>
                            آماده‌ی تحویل {MEAL_LABELS[mealType]}
                        </Typography>
                        <Typography sx={{ fontFamily: M.font, color: M.textMuted, fontSize: '1.1rem', mt: 1 }}>
                            کارت را به کارت‌خوان نزدیک کنید، یا کد پرسنلی را بنویسید و Enter بزنید
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    }

    const cfg = SCAN_KIND[entry.kind];
    const c = toneColors(cfg.tone);
    const title = entry.kind === 'delivered' && entry.isGroup
        ? 'تحویل گروهی انجام شد'
        : entry.kind === 'delivered' && entry.isGuestToken
            ? 'ژتون مهمان پذیرفته شد'
            : cfg.title;
    const canUndo = undoStateOf(entry) === 'available';
    const personName = entry.user ? `${entry.user.firstName} ${entry.user.lastName}` : null;

    return (
        <Grow in key={entry.id} timeout={reducedMotion ? 0 : 220}>
            <Box
                sx={{
                    flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden',
                    bgcolor: c.soft, border: `2px solid ${c.main}`, borderRadius: M.radiusCard, boxShadow: M.cardShadow,
                }}
            >
                <Typography role="status" aria-live="assertive" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                    {`${title}${personName ? `: ${personName}` : ''}`}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, px: 3, pt: 3, pb: 2 }}>
                    <Box sx={{ width: 84, height: 84, flexShrink: 0, borderRadius: '50%', bgcolor: c.main, color: '#0b1024', display: 'grid', placeItems: 'center', '& svg': { fontSize: 56 } }}>
                        {ICONS[entry.kind]}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontFamily: M.font, fontWeight: 900, fontSize: '2.2rem', lineHeight: 1.25, color: c.text }}>
                            {title}
                        </Typography>
                        <Typography sx={{ fontFamily: M.font, fontSize: '1.1rem', color: M.text, mt: 0.5 }}>
                            {detailLine(entry)}
                        </Typography>
                    </Box>
                    <Typography sx={{ fontFamily: M.font, color: M.textFaint, fontSize: '0.95rem', alignSelf: 'flex-start', fontVariantNumeric: 'tabular-nums' }}>
                        {formatClock(entry.at, true)}
                    </Typography>
                </Box>

                <Box sx={{ px: 3, pb: 3, flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2, '& > *': { flexShrink: 0 } }}>
                    {entry.isGroup && entry.groupTitle && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <GroupIcon sx={{ color: M.textMuted, fontSize: 36 }} />
                            <Typography sx={{ fontFamily: M.font, fontWeight: 800, fontSize: '2rem', color: M.text }}>{entry.groupTitle}</Typography>
                        </Box>
                    )}

                    {personName && (
                        <Box>
                            <Typography sx={{ fontFamily: M.font, fontWeight: 800, fontSize: '2.1rem', color: M.text, lineHeight: 1.3 }}>
                                {personName}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 0.5 }}>
                                {entry.user?.personnelCode && (
                                    <Typography sx={{ fontFamily: M.font, color: M.textMuted, fontSize: '1.05rem' }}>
                                        کد پرسنلی: <Box component="span" sx={{ color: M.text, fontWeight: 700 }}>{toPersianNumber(entry.user.personnelCode)}</Box>
                                    </Typography>
                                )}
                                {entry.user?.unitTitle && (
                                    <Typography sx={{ fontFamily: M.font, color: M.textMuted, fontSize: '1.05rem' }}>
                                        واحد: <Box component="span" sx={{ color: M.text, fontWeight: 700 }}>{entry.user.unitTitle}</Box>
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}

                    {entry.isGuestToken && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <StatusPill tone="info" icon={<TokenIcon />}>ژتون مهمان</StatusPill>
                            {entry.description && (
                                <Typography sx={{ fontFamily: M.font, color: M.textMuted, fontSize: '1.05rem' }}>{entry.description}</Typography>
                            )}
                        </Box>
                    )}

                    {(entry.kind === 'notFound' || entry.kind === 'invalidToken') && !personName && !entry.isGroup && entry.identifier && (
                        <Typography dir="ltr" sx={{ fontFamily: M.font, fontWeight: 800, fontSize: '1.6rem', color: M.text, letterSpacing: 2, textAlign: 'right' }}>
                            {toPersianNumber(entry.identifier)}
                        </Typography>
                    )}

                    {entry.meals && entry.meals.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {entry.meals.map((m) => (
                                <Box key={m.title} sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1.25, bgcolor: 'rgba(11,16,36,0.55)', borderRadius: M.radiusControl }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <MealThumb imageUrl={m.imageUrl} title={m.title} size={64} />
                                        <Typography sx={{ fontFamily: M.font, fontWeight: 700, fontSize: '1.45rem', color: M.text, flex: 1 }}>{m.title}</Typography>
                                        <Typography sx={{ fontFamily: M.font, fontWeight: 900, fontSize: '1.9rem', color: c.text, fontVariantNumeric: 'tabular-nums' }}>
                                            × {toPersianNumber(m.quantity)}
                                        </Typography>
                                    </Box>
                                    {m.members && m.members.length > 0 && (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                                            {m.members.map((member, idx) => (
                                                <Typography key={idx} sx={{ fontFamily: M.font, color: M.textMuted, fontSize: '0.95rem', bgcolor: 'rgba(255,255,255,0.05)', px: 1, py: 0.5, borderRadius: 1 }}>
                                                    {member.firstName} {member.lastName}
                                                </Typography>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    )}

                    {canUndo && (
                        <Button
                            onClick={() => onUndo(entry)}
                            startIcon={<UndoIcon />}
                            variant="outlined"
                            sx={{
                                alignSelf: 'flex-start', height: M.controlHeight, px: 2.5, borderRadius: M.radiusControl,
                                fontFamily: M.font, fontWeight: 700, color: M.dangerText, borderColor: `${M.danger}88`,
                                '&:hover': { borderColor: M.danger, bgcolor: M.dangerSoft },
                            }}
                        >
                            لغو این تحویل
                        </Button>
                    )}
                </Box>

                {cfg.dismissMs && !reducedMotion && (
                    <Box
                        aria-hidden
                        sx={{
                            position: 'absolute', insetInline: 0, bottom: 0, height: 4, bgcolor: c.main, transformOrigin: 'right',
                            animation: `${shrink} ${cfg.dismissMs}ms linear forwards`,
                            animationPlayState: countdownPaused ? 'paused' : 'running',
                        }}
                    />
                )}
            </Box>
        </Grow>
    );
};

export default ScanResultPanel;
